import { apiAxios } from '@/api'
import { toMonthDisplay } from './statementUpload'
import { plural } from '../../lib/plural'
import type { PollSchedule } from './polling'

export interface WaitAccount {
  financialAccountId: number
  accountLabel: string
  pending: boolean
  checkedMonths: string[]
  transactionCount: number
  unmatchedMonths: string[]
  withBookkeeperMonths: string[]
  awaitingStatementMonths: string[]
  beforeFirstActivityMonths: string[]
  noMonthsReason: NoMonthsReason | null
}

/** Mirrors the API's `NoMonthsReason`. */
type NoMonthsReason = 'no_catch_up_window' | 'books_not_opened'

export interface WaitStatus {
  pending: boolean
  accounts: WaitAccount[]
}

/**
 * Each poll runs the full catch-up status, so the gaps widen: an uploaded
 * statement usually settles within seconds, a Plaid statement rarely does.
 */
export const POLL_SCHEDULE: PollSchedule = {
  steps: [
    [30_000, 5_000],
    [2 * 60_000, 15_000],
  ],
  restMs: 30_000,
}

export const toFinancialAccountIds = (
  input: Record<string, unknown>
): number[] =>
  Array.isArray(input.financialAccountIds)
    ? input.financialAccountIds.filter(
        (id): id is number => typeof id === 'number' && Number.isInteger(id)
      )
    : []

export const fetchWaitStatus = async (
  financialAccountIds: number[]
): Promise<WaitStatus> => {
  const { data } = await apiAxios.get<WaitStatus>(
    '/finances/api/v1/user_documents/statement/reconciliation_wait_status',
    financialAccountIds.length
      ? { params: { financialAccountIds: financialAccountIds.join(',') } }
      : undefined
  )
  return data
}

const NO_MONTHS_DISPLAY: Record<NoMonthsReason, string> = {
  books_not_opened: 'books not opened yet',
  no_catch_up_window: 'no months to catch up',
}

export const hasOutstanding = (account: WaitAccount): boolean =>
  account.unmatchedMonths.length > 0 ||
  account.withBookkeeperMonths.length > 0 ||
  account.awaitingStatementMonths.length > 0 ||
  account.beforeFirstActivityMonths.length > 0 ||
  account.noMonthsReason === 'books_not_opened'

const isMatched = (account: WaitAccount): boolean =>
  account.checkedMonths.length > 0 && !hasOutstanding(account)

export type HeadlineStatus = 'matched' | 'attention' | 'neutral'

/** A green check needs at least one matched account and nothing outstanding. */
export const toHeadlineStatus = (accounts: WaitAccount[]): HeadlineStatus => {
  if (accounts.some(hasOutstanding)) return 'attention'
  return accounts.some(isMatched) ? 'matched' : 'neutral'
}

export const toBalanceDisplay = (account: WaitAccount): string => {
  const unmatched = account.unmatchedMonths.length
  if (unmatched > 0)
    return `${plural(unmatched, 'month')} ${unmatched === 1 ? "doesn't" : "don't"} match yet`
  if (account.withBookkeeperMonths.length > 0) return 'with your bookkeeper'
  if (account.awaitingStatementMonths.length > 0)
    return `${plural(account.awaitingStatementMonths.length, 'statement')} still needed`
  if (account.beforeFirstActivityMonths.length > 0)
    return `${plural(account.beforeFirstActivityMonths.length, 'month')} to confirm`
  return account.checkedMonths.length > 0
    ? 'balances match'
    : 'nothing checked yet'
}

export const toAccountDetail = (account: WaitAccount): string => {
  if (account.noMonthsReason) return NO_MONTHS_DISPLAY[account.noMonthsReason]
  return [
    plural(account.checkedMonths.length, 'month'),
    plural(account.transactionCount, 'transaction'),
    toBalanceDisplay(account),
  ].join(' · ')
}

export const toHeadline = (accounts: WaitAccount[]): string => {
  const months = new Set(accounts.flatMap(({ checkedMonths }) => checkedMonths))
  return `Checked ${plural(accounts.length, 'account')} against ${plural(months.size, 'month')} of bank records`
}

const listMonths = (months: string[]): string =>
  months.map(toMonthDisplay).join(', ')

const describeSettled = (account: WaitAccount): string => {
  if (account.noMonthsReason)
    return `${account.accountLabel}: ${NO_MONTHS_DISPLAY[account.noMonthsReason]}.`
  const parts = [
    `${plural(account.checkedMonths.length, 'month')} checked`,
    account.unmatchedMonths.length &&
      `not matched: ${listMonths(account.unmatchedMonths)}`,
    account.withBookkeeperMonths.length &&
      `with a bookkeeper: ${listMonths(account.withBookkeeperMonths)}`,
    account.awaitingStatementMonths.length &&
      `awaiting a statement: ${listMonths(account.awaitingStatementMonths)}`,
    account.beforeFirstActivityMonths.length &&
      `before its first transaction, for the member to confirm: ${listMonths(account.beforeFirstActivityMonths)}`,
  ].filter(Boolean)
  return `${account.accountLabel}: ${parts.join('; ')}.`
}

const NOTHING_TO_WAIT_ON_OUTCOME = 'There were no accounts to wait on.'

export const toOutcome = (status: WaitStatus): string => {
  if (status.accounts.length === 0) return NOTHING_TO_WAIT_ON_OUTCOME
  const stillPending = status.accounts.filter(({ pending }) => pending)
  const lines = status.accounts
    .filter(({ pending }) => !pending)
    .map(describeSettled)
  if (stillPending.length > 0) {
    lines.unshift(
      `Still waiting on ${stillPending.map(({ accountLabel }) => accountLabel).join(', ')}. Heard has not finished matching their statements yet.`
    )
  }
  return lines.join('\n')
}
